import { AuthProvider, useAuth } from "@keel/core";
import { EntryDrawerHost, NumberPromptHost, Skeleton, ToastProvider } from "@keel/ui";
import "@keel/ui/styles.css";
import { BorrowerPortal } from "@keel/module-loans";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { activePlugins } from "./modules";
import { Dashboard } from "./pages/Dashboard";
import { HelpPage } from "./pages/Help";
import { Login } from "./pages/Login";
import { NoTenant } from "./pages/NoTenant";
import { WhatsNewPage } from "./pages/WhatsNew";
import { Shell } from "./Shell";

/** Home plus the routes of every module this account has; module pages load on first visit. */
function AppRoutes() {
  const { me } = useAuth();
  return (
    <Suspense fallback={<Skeleton />}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/help/:module/:slug" element={<HelpPage />} />
        <Route path="/whats-new" element={<WhatsNewPage />} />
        {activePlugins(me.modules)
          .flatMap((p) => p.routes)
          .map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 10_000, retry: 1, refetchOnWindowFocus: false } },
});

// A number field selects what it holds when you click or tab into it, so typing replaces the value:
// clicking into a "0" and typing 1 gave "10" (ERP-47). Once per app, for every form.
document.addEventListener("focusin", (e) => {
  const el = e.target;
  if (el instanceof HTMLInputElement && el.type === "number" && !el.readOnly) {
    // After the click has placed the caret, or it would undo the selection.
    requestAnimationFrame(() => {
      if (document.activeElement === el) el.select();
    });
  }
});

// The borrower portal (ERP-81) is its own small app: a borrower is not a member of the account, so
// none of the staff sign-in, menus or session apply.
const portal = window.location.pathname.startsWith("/portal/");

createRoot(document.getElementById("root")!).render(
  portal ? (
    <StrictMode>
      <BrowserRouter>
        <Suspense fallback={<Skeleton />}>
          <Routes>
            <Route path="/portal/*" element={<BorrowerPortal />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </StrictMode>
  ) : (
    <StrictMode>
      <QueryClientProvider client={qc}>
        <ToastProvider>
          <BrowserRouter>
            <AuthProvider fallback={<Login />} noTenant={<NoTenant />}>
              <EntryDrawerHost>
                <Shell>
                  <AppRoutes />
                </Shell>
                <NumberPromptHost />
              </EntryDrawerHost>
            </AuthProvider>
          </BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </StrictMode>
  ),
);
